/**
 * Portfolio Capacity Report — a branded, stakeholder-ready PDF built purely
 * client-side (jsPDF) from the `/api/portfolio/resource-heatmap` payload.
 * One click, no login needed for the recipient: the manager downloads the
 * file and shares it. Cover + KPI summary + over-allocation callout +
 * per-person capacity table (paginated).
 */
import { jsPDF } from 'jspdf';

const BRAND_NAVY = [15, 32, 64];
const BRAND_TEAL = [74, 191, 207];
const SLATE_500 = [100, 116, 139];
const SLATE_700 = [51, 65, 85];
const SLATE_400 = [148, 163, 184];
const SLATE_200 = [226, 232, 240];
const SLATE_50 = [248, 250, 252];
const ROSE_BG = [254, 226, 226];
const ROSE_FG = [190, 18, 60];

function drawHeader(doc, label) {
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(...BRAND_NAVY); doc.rect(0, 0, w, 32, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
  doc.text(label, 54, 20);
}

function drawFooter(doc) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  const page = doc.internal.getNumberOfPages();
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
  doc.setTextColor(...SLATE_400);
  doc.text(`Generated ${new Date().toLocaleString()}  \u2022  Seertech Blueprint Studio`, w / 2, h - 24, { align: 'center' });
  doc.text(`Page ${page}`, w - 54, h - 24, { align: 'right' });
}

const roleLabel = (rc, labels) => labels?.[rc] || rc;

export function exportCapacityReport(data, options = {}) {
  const { windowLabel = '' } = options;
  const resources = (data?.resources || []).slice().sort((a, b) => (b.peak_pct || 0) - (a.peak_pct || 0));
  const summary = data?.summary || {};
  const roleLabels = data?.role_labels || {};

  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  doc.setProperties({
    title: 'Portfolio Capacity Report',
    subject: 'Per-person committed FTE and over-allocation across live projects',
    author: 'Seertech Blueprint Studio',
    creator: 'Seertech Blueprint Studio',
    keywords: ['Seertech', 'Capacity', 'Resource Planning', 'Over-allocation'].join(', '),
  });

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 54;
  const contentW = pageW - margin * 2;

  drawHeader(doc, 'SEERTECH \u2014 PORTFOLIO CAPACITY REPORT');

  // Title block
  let y = 90;
  doc.setTextColor(...BRAND_NAVY); doc.setFontSize(24); doc.setFont('helvetica', 'bold');
  doc.text('Portfolio Capacity Report', margin, y);
  y += 20;
  doc.setFontSize(11); doc.setTextColor(...SLATE_500); doc.setFont('helvetica', 'normal');
  const rangeText = data?.start_date && data?.end_date
    ? `${data.start_date} \u2192 ${data.end_date} (${data.weeks_n} weeks)`
    : `${data?.weeks_n || ''} week window`;
  doc.text(`Committed FTE across all live projects  \u2022  ${windowLabel || rangeText}`, margin, y);
  y += 12;

  doc.setDrawColor(...BRAND_TEAL); doc.setLineWidth(2);
  doc.line(margin, y, margin + 80, y);
  doc.setLineWidth(1);
  y += 24;

  // KPI grid (2x2)
  const stats = [
    ['People', String(summary.total_resources ?? resources.length)],
    ['Projects in scope', String(summary.estimates_in_scope ?? '\u2014')],
    ['Over-allocated people', String(summary.over_allocated_people ?? 0)],
    ['Over-allocated cells', String(summary.over_allocated_cells ?? 0)],
  ];
  const cellW = contentW / 2;
  stats.forEach(([label, value], i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x = margin + col * cellW;
    const cy = y + row * 58;
    const isRisk = i >= 2 && Number(value) > 0;
    if (isRisk) { doc.setFillColor(...ROSE_BG); doc.roundedRect(x, cy, cellW - 10, 48, 4, 4, 'F'); }
    else { doc.setDrawColor(...SLATE_200); doc.roundedRect(x, cy, cellW - 10, 48, 4, 4); }
    doc.setTextColor(...(isRisk ? ROSE_FG : SLATE_500)); doc.setFontSize(8); doc.setFont('helvetica', 'bold');
    doc.text(label.toUpperCase(), x + 12, cy + 16);
    doc.setTextColor(...(isRisk ? ROSE_FG : BRAND_NAVY)); doc.setFontSize(18); doc.setFont('helvetica', 'bold');
    doc.text(String(value), x + 12, cy + 38);
  });
  y += 130;

  // Over-allocated callout
  const over = resources.filter(r => (r.over_allocated_week_indices || []).length > 0);
  if (over.length > 0) {
    doc.setTextColor(...ROSE_FG); doc.setFontSize(11); doc.setFont('helvetica', 'bold');
    doc.text(`\u26A0  Over-allocated (${over.length})`, margin, y);
    y += 14;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...SLATE_700);
    for (const r of over.slice(0, 6)) {
      doc.text(`\u2022  ${r.name} \u2014 peak ${Math.round(r.peak_pct)}% over ${r.over_allocated_week_indices.length} week(s)`, margin + 4, y);
      y += 12;
    }
    if (over.length > 6) {
      doc.setTextColor(...SLATE_400); doc.setFont('helvetica', 'italic');
      doc.text(`\u2026 and ${over.length - 6} more (see table)`, margin + 4, y);
      y += 12;
    }
    y += 8;
  }

  // ── Per-person capacity table ──────────────────────────────────
  const cols = [
    { key: 'name', label: 'Person', x: margin, w: 176 },
    { key: 'roles', label: 'Roles', x: margin + 176, w: 150 },
    { key: 'peak', label: 'Peak FTE', x: margin + 326, w: 70, align: 'right' },
    { key: 'overw', label: 'Over wks', x: margin + 396, w: 55, align: 'right' },
    { key: 'commw', label: 'Committed', x: margin + 451, w: 55, align: 'right' },
  ];
  const rowH = 20;
  const bottom = pageH - 50;

  const drawTableHeader = () => {
    doc.setFillColor(...BRAND_NAVY);
    doc.rect(margin, y, contentW, 20, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont('helvetica', 'bold');
    cols.forEach(c => doc.text(c.label.toUpperCase(), c.align === 'right' ? c.x + c.w - 6 : c.x + 6, y + 13, { align: c.align || 'left' }));
    y += 20;
  };

  doc.setTextColor(...BRAND_NAVY); doc.setFontSize(11); doc.setFont('helvetica', 'bold');
  doc.text(`Capacity by person (${resources.length})`, margin, y);
  y += 12;
  drawTableHeader();

  resources.forEach((r, i) => {
    if (y + rowH > bottom) {
      drawFooter(doc);
      doc.addPage();
      drawHeader(doc, 'SEERTECH \u2014 PORTFOLIO CAPACITY REPORT');
      y = 60;
      drawTableHeader();
    }
    const isOver = (r.over_allocated_week_indices || []).length > 0;
    if (isOver) doc.setFillColor(...ROSE_BG);
    else doc.setFillColor(...(i % 2 === 0 ? SLATE_50 : [255, 255, 255]));
    doc.rect(margin, y, contentW, rowH, 'F');

    doc.setFont('helvetica', isOver ? 'bold' : 'normal'); doc.setFontSize(9);
    doc.setTextColor(...(isOver ? ROSE_FG : SLATE_700));
    const name = doc.splitTextToSize(r.name || '\u2014', cols[0].w - 10)[0];
    doc.text(name, cols[0].x + 6, y + 13);

    doc.setFont('helvetica', 'normal'); doc.setTextColor(...SLATE_500); doc.setFontSize(8);
    const roles = (r.roles || []).map(rc => roleLabel(rc, roleLabels)).join(', ') || '\u2014';
    doc.text(doc.splitTextToSize(roles, cols[1].w - 10)[0], cols[1].x + 6, y + 13);

    doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    doc.setTextColor(...(isOver ? ROSE_FG : BRAND_NAVY));
    doc.text(`${Math.round(r.peak_pct || 0)}%`, cols[2].x + cols[2].w - 6, y + 13, { align: 'right' });

    doc.setFont('helvetica', 'normal'); doc.setTextColor(...SLATE_700);
    doc.text(String((r.over_allocated_week_indices || []).length || '\u2013'), cols[3].x + cols[3].w - 6, y + 13, { align: 'right' });
    doc.text(String(r.total_committed_weeks ?? '\u2013'), cols[4].x + cols[4].w - 6, y + 13, { align: 'right' });

    y += rowH;
  });

  drawFooter(doc);

  const stamp = new Date().toISOString().slice(0, 10);
  const filename = `portfolio-capacity-report-${stamp}.pdf`;
  doc.save(filename);
  return filename;
}
